import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Grid, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useServiceCatalog } from '../contexts/ServiceCatalogContext';
import {
  CleaningServices,
  HomeWork,
  AutorenewRounded,
  CleanHands,
  EventAvailable,
  BeachAccess
} from '@mui/icons-material';

// Define service icons and images mapping
const serviceConfig = {
  'svc_post_construction_cleaning': {
    icon: <HomeWork sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_post_construction_cleaning.webp'
  },
  'svc_move-in-out-clean': {
    icon: <CleaningServices sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_move-in-out-clean.webp'
  },
  'svc_recurring': {
    icon: <AutorenewRounded sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_recurring.webp'
  },
  'svc_deep-clean': {
    icon: <CleanHands sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_deep-clean.webp'
  },
  'svc_one-time-clean': {
    icon: <EventAvailable sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_one-time-clean.webp'
  },
  'svc_vacation-rental-turnover-clean': {
    icon: <BeachAccess sx={{ fontSize: 48 }} />,
    image: '/media/services/svc_vacation-rental-turnover-clean.webp'
  }
};

const ServicesGrid = ({ maxServices = null, showTitle = true }) => {
  const { services: allServices, loading } = useServiceCatalog();
  const navigate = useNavigate();
  
  // Apply maxServices limit to the services from context
  const services = maxServices && maxServices > 0 
    ? allServices?.slice(0, maxServices) 
    : allServices;

  // Services data now managed by ServiceCatalogContext

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {showTitle && (
          <Typography variant="h3" component="h2" align="center" sx={{ mb: 4, fontWeight: 'bold' }}>
            Cleaning Services
          </Typography>
        )}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="h6" color="text.secondary">
            Loading services...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (services.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        {showTitle && (
          <Typography variant="h3" component="h2" align="center" sx={{ mb: 4, fontWeight: 'bold' }}>
            Cleaning Services
          </Typography>
        )}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography variant="h6" color="error.main">
            Unable to load services. Please try again later.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      {showTitle && (
        <Typography variant="h3" component="h2" align="center" sx={{ mb: 4, fontWeight: 'bold', color: 'text.primary' }}>
          Our Core Services
        </Typography>
      )}
      
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
        {services.map((service) => {
          const config = serviceConfig[service.id] || {
            icon: <CleaningServices sx={{ fontSize: 48 }} />,
            image: '/media/services/svc_deep-clean.webp' // Default fallback image
          };

          return (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <Card
                sx={{
                  background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${config.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  color: 'white',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${config.image})`,
                  },
                  minHeight: { xs: 200, sm: 250, md: 300 },
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={() => handleServiceClick(service.id)}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    p: { xs: 2, sm: 2.5, md: 3 },
                    flex: 1,
                    position: 'relative',
                    zIndex: 2,
                    '&:last-child': {
                      pb: { xs: 2, sm: 2.5, md: 3 }
                    }
                  }}
                >
                  <Box sx={{ 
                    mb: { xs: 1.5, sm: 2 }, 
                    opacity: 0.9,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {config.icon}
                  </Box>
                  
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      textAlign: 'center',
                      lineHeight: 1.3,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                    }}
                  >
                    {service.service_name}
                  </Typography>
                  
                  {service.tagline && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        opacity: 0.9,
                        fontSize: '0.85rem',
                        fontStyle: 'italic',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                      }}
                    >
                      {service.tagline}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {maxServices && services.length >= maxServices && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'primary.main', 
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': {
                opacity: 0.7
              }
            }}
            onClick={() => navigate('/services')}
          >
            View All Services →
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ServicesGrid;